import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { DueDiligenceTier } from "../../../../Common/Types-DTOs/DueDiligence";

const TIER_ORDER: DueDiligenceTier[] = ["basic", "professional", "premium"];

type Pricing = { basic: number | null; professional: number | null; premium: number | null };

interface Props {
  currentTier: DueDiligenceTier;
  pricing?: Pricing | null;
  /** Upgrades are paid with credits, priced as the difference in them: subscribers. */
  creditCosts?: Record<DueDiligenceTier, number> | null;
  /** Asks to upgrade to a higher tier. Absent when the case can't be upgraded. */
  onUpgrade?: (tier: DueDiligenceTier) => void;
}

const Track = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: #f0f3f1;
  border: 1px solid #e7ecea;
  border-radius: 10px;
  flex-shrink: 0;
`;

const Pill = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  white-space: nowrap;
  background: ${(p) => (p.$active ? "#14323B" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "#7C8B91")};
  box-shadow: ${(p) => (p.$active ? "0 1px 3px rgba(20,50,59,0.18)" : "none")};
  transition: all 0.13s;
  cursor: default;

  &:not(:disabled) {
    cursor: pointer;
  }
  &:not(:disabled):hover {
    background: #fff;
    color: #14323b;
  }
`;

const Price = styled.span<{ $active: boolean }>`
  font-size: 10px;
  color: ${(p) => (p.$active ? "rgba(255,255,255,0.62)" : "#AAB6B1")};
`;

/**
 * The report header's tier switch: one pill per tier, the current one filled.
 * Higher tiers are the upsell, priced as what an upgrade actually costs: the
 * difference with the current tier, in credits for subscribers. Clicking one
 * asks for the upgrade, which is confirmed elsewhere.
 */
export const ReportTierControl = ({ currentTier, pricing, creditCosts, onUpgrade }: Props) => {
  const { t } = useTranslation();
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  const currentPrice = pricing?.[currentTier];

  const differenceLabel = (tier: DueDiligenceTier): string | null => {
    if (creditCosts) {
      return t("dueDiligence.legalFiscalReport.reportSubheader.creditsDifference", {
        count: creditCosts[tier] - creditCosts[currentTier],
      });
    }
    const price = pricing?.[tier];
    return price != null && currentPrice != null ? `+${price - currentPrice} €` : null;
  };

  return (
    <Track>
      {TIER_ORDER.map((tier, index) => {
        const active = tier === currentTier;
        const upgradable = index > currentIndex && !!onUpgrade;
        const difference = upgradable ? differenceLabel(tier) : null;

        return (
          <Pill
            key={tier}
            type="button"
            $active={active}
            disabled={!upgradable}
            title={t(`dueDiligence.caseDetails.tierLabels.${tier}`)}
            onClick={() => onUpgrade?.(tier)}
          >
            <span>{t("dueDiligence.legalFiscalReport.reportSubheader.tierLevel", { level: index + 1 })}</span>
            {difference != null && <Price $active={active}>{difference}</Price>}
          </Pill>
        );
      })}
    </Track>
  );
};
